/**
 * Backend error-code → user-facing message map.
 *
 * Every code is derived from the backend contract (Phase 3 + Phase 4).
 * Renderers must show BOTH the mono `code` chip AND the friendly message —
 * never paraphrase the code away. Unknown codes fall back to a generic
 * copy while still surfacing the raw code chip via the caller.
 *
 * `resolve(error)` returns:
 *    { code, message, hint, isKnown, isEntitlement, details }
 *
 * - error may be an `ApiError` (has code + details) or a plain object.
 * - hint is a caller-facing next-action string. Not always shown.
 * - isEntitlement true only for ENTITLEMENT_EXCEEDED (special CTA path).
 */

export const ERROR_CODES = {
  // --- Auth / session ------------------------------------------------------
  UNAUTHENTICATED: {
    message: "You're signed out. Please sign in again to continue.",
    hint: "Return to the sign-in page.",
  },
  INVALID_CREDENTIALS: {
    message: "That email or password doesn't match. Try again.",
    hint: "Contact your workspace admin if you've forgotten your password.",
  },
  REGISTRATION_UNAVAILABLE: {
    message: "Self-service sign-up is turned off on this instance.",
    hint: "Ask your workspace admin to invite you.",
  },
  FORBIDDEN: {
    message: "Your role doesn't grant access to this action.",
    hint: "Ask a workspace admin if you believe this is wrong.",
  },
  // --- Not-found (tenant-safe) --------------------------------------------
  NOT_FOUND: {
    message: "That page or resource doesn't exist.",
    hint: "It may have been deleted, or you may not have access.",
  },
  WORKSPACE_NOT_FOUND: {
    message: "That workspace doesn't exist, or you're not a member.",
    hint: "Pick a workspace from the switcher.",
  },
  RUN_NOT_FOUND: {
    message: "That analysis run doesn't exist in this workspace.",
    hint: "Return to the Analyses list.",
  },
  PROCESS_ANALYSIS_NOT_FOUND: {
    message: "That process wasn't analysed in this run.",
    hint: "It may have been added after the run completed.",
  },
  DATASET_NOT_FOUND: {
    message: "The canonical dataset for that run isn't available.",
    hint: "The run may still be initialising, or the dataset was purged.",
  },
  FINDING_NOT_FOUND: {
    message: "That finding is no longer available.",
    hint: "Findings are pinned to a specific run.",
  },
  IMPORT_NOT_FOUND: {
    message: "That import doesn't exist in this workspace.",
    hint: "Start a fresh import from the Import wizard.",
  },
  MAPPING_PROFILE_NOT_FOUND: {
    message: "That mapping profile no longer exists.",
    hint: "Pick a different profile or continue without one.",
  },
  USER_NOT_FOUND: {
    message: "No user with that email is registered on the platform.",
    hint: "Ask the person to sign in once before inviting.",
  },
  MEMBERSHIP_NOT_FOUND: {
    message: "That person isn't a member of this workspace.",
  },
  // --- Validation / method errors -----------------------------------------
  VALIDATION_ERROR: {
    message: "The submitted values didn't pass validation.",
    hint: "Fix the highlighted fields and try again.",
  },
  METHOD_NOT_ALLOWED: {
    message: "That request method isn't supported on this route.",
  },
  CONFLICT: {
    message: "That change conflicts with the current state.",
    hint: "Reload the page and try again.",
  },
  RATE_LIMITED: {
    message: "Too many requests. Please wait a moment and try again.",
  },
  HTTP_ERROR: {
    message: "Something went wrong on the server. Please try again.",
  },
  INTERNAL_ERROR: {
    message: "Something went wrong on the server. Please try again.",
    hint: "If it keeps happening, contact support with the code below.",
  },
  NETWORK_ERROR: {
    message: "We couldn't reach the server. Check your connection.",
  },
  // --- Entitlements -------------------------------------------------------
  ENTITLEMENT_EXCEEDED: {
    message: "Your plan limits were reached.",
    hint: "Upgrade the workspace plan or contact us to raise the limit.",
  },
  // --- Import pipeline (23) -----------------------------------------------
  IMPORT_UNSUPPORTED_FORMAT: {
    message: "That file type isn't supported. Upload an .xlsx or .csv file.",
  },
  IMPORT_FILE_TOO_LARGE: {
    message: "That file is larger than the workspace limit.",
    hint: "Split the workbook into smaller files.",
  },
  IMPORT_ROW_LIMIT_EXCEEDED: {
    message: "The workbook has more rows than the import limit.",
    hint: "Remove empty rows or split into smaller files.",
  },
  IMPORT_SHEET_LIMIT_EXCEEDED: {
    message: "The workbook has more sheets than the import limit.",
  },
  IMPORT_COL_LIMIT_EXCEEDED: {
    message: "One or more sheets have more columns than the import limit.",
  },
  IMPORT_INVALID_WORKBOOK: {
    message: "That workbook couldn't be parsed.",
    hint: "Open it in Excel, re-save, and try again.",
  },
  IMPORT_MACRO_OR_EMBEDDED_CONTENT: {
    message: "Workbooks with macros or embedded objects are rejected.",
    hint: "Re-save as a plain .xlsx without VBA / OLE content.",
  },
  IMPORT_PARSE_TIMEOUT: {
    message: "Parsing that workbook took too long.",
    hint: "Try a smaller file or split it up.",
  },
  IMPORT_MAPPING_INCOMPLETE: {
    message: "The mapping is missing a required entity or field.",
    hint: "Fill in every required sheet-to-entity and column mapping.",
  },
  IMPORT_AMBIGUOUS_DURATION: {
    message:
      "A duration value is ambiguous (e.g. \"1 business day\"). We won't guess.",
    hint: "Rewrite in explicit units (240 minutes, 4h, 2 days, PT4H).",
  },
  IMPORT_INVALID_PERCENTAGE: {
    message: "A percentage value is invalid.",
    hint: "Use whole numbers 0–100 (or 0–1 if the column is a fraction).",
  },
  IMPORT_REQUIRED_FIELD_MISSING: {
    message:
      "A required field is missing on one or more rows.",
    hint: "Fill in every required cell before continuing.",
  },
  IMPORT_UNKNOWN_REFERENCE: {
    message: "A row refers to an id that doesn't exist in this dataset.",
    hint: "Check the highlighted rows in the preview report.",
  },
  IMPORT_DUPLICATE_ID_CONFLICT: {
    message: "Two rows share the same id in the same entity.",
    hint: "Give each row a unique id.",
  },
  IMPORT_ENUM_MAPPING_REQUIRED: {
    message:
      "Some enum values (e.g. Criticality, boolean fields) still need to be mapped.",
    hint: "Complete the value-mapping step before continuing.",
  },
  IMPORT_SOURCE_CONFLICT: {
    message: "The same field has conflicting values across sources.",
    hint: "Resolve conflicts by editing the source workbook.",
  },
  IMPORT_STATE_INVALID: {
    message: "That step was called out of order for this import.",
    hint: "Restart the wizard from the current step.",
  },
  IMPORT_UNKNOWN_ENTITY: {
    message: "A sheet is mapped to an entity we don't recognise.",
    hint: "Pick one of the canonical entities from the dropdown.",
  },
  IMPORT_UNKNOWN_FIELD: {
    message: "A column is mapped to a field we don't recognise.",
    hint: "Pick a canonical field or leave the column ignored.",
  },
  IMPORT_INVALID_BOOLEAN: {
    message: "A boolean value couldn't be interpreted.",
    hint: "Map the source value in the value-mapping step.",
  },
  IMPORT_INVALID_INTEGER: {
    message: "An integer value couldn't be parsed.",
    hint: "Remove non-numeric characters from the cell.",
  },
  IMPORT_INVALID_DATE: {
    message: "A date value couldn't be parsed.",
    hint: "Use ISO 8601 (YYYY-MM-DD) or an Excel date cell.",
  },
  IMPORT_SELF_DEPENDENCY: {
    message: "A process cannot depend on itself.",
    hint: "Remove the self-referencing row in ProcessDependencies.",
  },
};

const FALLBACK_MESSAGE =
  "Something didn't work. If this keeps happening, share the code below with support.";

export function resolveError(err) {
  const code = (err && err.code) || "UNKNOWN_ERROR";
  const known = ERROR_CODES[code];
  const isEntitlement = code === "ENTITLEMENT_EXCEEDED";
  const details = (err && err.details) || {};
  let message = known ? known.message : FALLBACK_MESSAGE;
  let hint = known ? known.hint : undefined;
  if (isEntitlement) {
    const cur = details.current_limit;
    const detected = details.detected_count;
    const field = details.field;
    if (field === "max_processes" && typeof cur === "number") {
      message = `Your Trial workspace supports up to ${cur} processes. This import contains ${detected} valid processes.`;
    } else if (field === "max_analyses" && typeof cur === "number") {
      message = `Your workspace already has ${cur} analyses — that's the plan cap. Delete an older run or upgrade to add more.`;
    }
  }
  return {
    code,
    message: (err && err.message && !known) ? err.message : message,
    hint,
    isKnown: !!known,
    isEntitlement,
    details,
  };
}
